class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    /* if (window.posthog) {
      trackFiltersApplied(searchParams);
    } */

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url, { cache: 'no-store' })
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        FacetFiltersForm.applyTrueTotal();
        FacetFiltersForm.refreshOpenLiveCountDetails();
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
      });
  }

  // The server can only count against whatever collection.products it was handed - accurate
  // with no shopper filter active, an undercount once Shopify has narrowed it for a filter (see
  // the data-true-total marker rendered in facets.liquid / main-collection-product-grid.liquid).
  // Fetching the same section with no filter params once gives the real, filter-invariant total,
  // which we then swap into every "of N" count on the page, whatever filters are applied.
  static fetchTrueTotal() {
    if (!FacetFiltersForm.trueTotalPromise) {
      const productGrid = document.getElementById('product-grid');
      const sectionId = productGrid && productGrid.dataset.id;
      FacetFiltersForm.trueTotalPromise = sectionId
        ? fetch(`${window.location.pathname}?section_id=${sectionId}`, { cache: 'no-store' })
            .then((response) => response.text())
            .then((html) => {
              const marker = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-true-total]');
              return marker ? marker.dataset.trueTotal : null;
            })
            .catch(() => null)
        : Promise.resolve(null);
    }
    return FacetFiltersForm.trueTotalPromise;
  }

  static applyTrueTotal() {
    FacetFiltersForm.fetchTrueTotal().then((trueTotal) => {
      if (!trueTotal) return;
      document.querySelectorAll('[data-true-total]').forEach((marker) => {
        const shownTotal = marker.dataset.shownTotal;
        const container = marker.closest('[id^="ProductCount"], .mobile-facets__count') || marker.parentElement;
        if (!container || !shownTotal || shownTotal === trueTotal) return;
        const pattern = new RegExp(`\\b${shownTotal}\\b`);
        if (pattern.test(container.textContent)) {
          container.textContent = container.textContent.replace(pattern, trueTotal);
        }
      });
    });
  }

  // Filters like Fit/Fabric Weight/Rise/Stretch are backed by Shopify "grouped" metafield
  // values (opaque FilterSettingGroup GIDs) - there's no way, in Liquid or JS, to map that GID
  // back to the raw metafield value, so the server can't compute a strict same-eligibility count
  // for them the way it does for Size/Inseam/Color. Instead, once the shopper opens one of these
  // filter groups (marked data-live-count="true"), we simulate checking each value in the
  // background - fetching the page as if that value + whatever's currently active were applied -
  // and count the real rendered products, then patch that exact number into the checkbox.
  static computeLiveCounts(detailsEl) {
    const productGrid = document.getElementById('product-grid');
    const sectionId = productGrid && productGrid.dataset.id;
    if (!sectionId) return;

    const baseParams = new URLSearchParams(window.location.search);
    const inputs = detailsEl.querySelectorAll('input[type="checkbox"]');

    inputs.forEach((input) => {
      if (!input.name || !input.value) return;

      const params = new URLSearchParams(baseParams);
      params.delete(input.name);
      params.set(input.name, input.value);
      const url = `${window.location.pathname}?section_id=${sectionId}&${params.toString()}`;

      if (FacetFiltersForm.liveCountCache.has(url)) {
        FacetFiltersForm.applyLiveCount(input, FacetFiltersForm.liveCountCache.get(url));
        return;
      }

      fetch(url, { cache: 'no-store' })
        .then((response) => response.text())
        .then((html) => {
          const parsedGrid = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductGridContainer');
          const count = parsedGrid ? parsedGrid.querySelectorAll('.grid__item').length : 0;
          FacetFiltersForm.liveCountCache.set(url, count);
          FacetFiltersForm.applyLiveCount(input, count);
        })
        .catch(() => {});
    });
  }

  static applyLiveCount(input, count) {
    const label = input.closest('label');
    if (!label) return;

    const visibleCount = label.querySelector('span[aria-hidden="true"]');
    if (visibleCount) visibleCount.textContent = visibleCount.textContent.replace(/\(\d+\)/, `(${count})`);

    const hiddenCount = label.querySelector('span.visually-hidden');
    if (hiddenCount) hiddenCount.textContent = hiddenCount.textContent.replace(/\d+/, count);

    const shouldDisable = count === 0 && !input.checked;
    input.disabled = shouldDisable;
    label.classList.toggle('facet-checkbox--disabled', shouldDisable);
    label.classList.toggle('mobile-facets__label--disabled', shouldDisable);
  }

  // Re-run the live-count fetches for any of these filter groups the shopper already had open
  // when a DIFFERENT filter changed (their counts depend on the full set of active filters).
  static refreshOpenLiveCountDetails() {
    document.querySelectorAll('details[data-live-count="true"][open]').forEach((detailsEl) => {
      FacetFiltersForm.computeLiveCounts(detailsEl);
    });
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

    const facetDetailsElements = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const matchesIndex = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.dataset.index === jsFilter.dataset.index : false;
    };
    const facetsToRender = Array.from(facetDetailsElements).filter((element) => !matchesIndex(element));
    const countsToRender = Array.from(facetDetailsElements).find(matchesIndex);

    facetsToRender.forEach((element) => {
      document.querySelector(`.js-filter[data-index="${element.dataset.index}"]`).innerHTML = element.innerHTML;
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const targetGroup = event.target.closest('.js-filter');
      FacetFiltersForm.renderCounts(countsToRender, targetGroup);
      FacetFiltersForm.updateFacetValueCounts(countsToRender, targetGroup);
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetElement = target.querySelector('.facets__selected');
    const sourceElement = source.querySelector('.facets__selected');

    const targetElementAccessibility = target.querySelector('.facets__summary');
    const sourceElementAccessibility = source.querySelector('.facets__summary');

    if (sourceElement && targetElement) {
      target.querySelector('.facets__selected').outerHTML = source.querySelector('.facets__selected').outerHTML;
    }

    if (targetElementAccessibility && sourceElementAccessibility) {
      target.querySelector('.facets__summary').outerHTML = source.querySelector('.facets__summary').outerHTML;
    }
  }

  // Updates each checkbox's own "(N)" count/disabled state within the filter group the shopper
  // just interacted with, without replacing the <input> itself (that would drop its checked/focus
  // state right after the click). renderCounts() above only refreshes the group's summary badge;
  // this covers the individual value rows so they don't stay frozen at whatever they were on load.
  static updateFacetValueCounts(source, target) {
    const sourceInputs = source.querySelectorAll('input[type="checkbox"]');

    sourceInputs.forEach((sourceInput) => {
      if (!sourceInput.id) return;
      const targetInput = target.querySelector(`#${CSS.escape(sourceInput.id)}`);
      if (!targetInput) return;

      const sourceLabel = sourceInput.closest('label');
      const targetLabel = targetInput.closest('label');
      if (!sourceLabel || !targetLabel) return;

      const sourceVisibleCount = sourceLabel.querySelector('span[aria-hidden="true"]');
      const targetVisibleCount = targetLabel.querySelector('span[aria-hidden="true"]');
      if (sourceVisibleCount && targetVisibleCount) {
        targetVisibleCount.textContent = sourceVisibleCount.textContent;
      }

      const sourceHiddenCount = sourceLabel.querySelector('span.visually-hidden');
      const targetHiddenCount = targetLabel.querySelector('span.visually-hidden');
      if (sourceHiddenCount && targetHiddenCount) {
        targetHiddenCount.textContent = sourceHiddenCount.textContent;
      }

      targetInput.disabled = sourceInput.disabled;
      targetLabel.classList.toggle('facet-checkbox--disabled', sourceInput.disabled);
      targetLabel.classList.toggle('mobile-facets__label--disabled', sourceInput.disabled);
    });
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            const noJsElements = document.querySelectorAll('.no-js-list');
            noJsElements.forEach((el) => el.remove());
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();
FacetFiltersForm.applyTrueTotal();

FacetFiltersForm.liveCountCache = new Map();
// 'toggle' on <details> doesn't bubble, so this has to listen during the capture phase.
document.addEventListener(
  'toggle',
  (event) => {
    const details = event.target;
    if (details.tagName === 'DETAILS' && details.dataset.liveCount === 'true' && details.open) {
      FacetFiltersForm.computeLiveCounts(details);
    }
  },
  true
);
document.querySelectorAll('details[data-live-count="true"][open]').forEach((detailsEl) => {
  FacetFiltersForm.computeLiveCounts(detailsEl);
});

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) =>
      element.addEventListener('change', this.onRangeChange.bind(this))
    );
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('min', 0);
    if (maxInput.value === '') minInput.setAttribute('max', maxInput.getAttribute('max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('min'));
    const max = Number(input.getAttribute('max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
